import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouteLoaderData } from 'react-router';
import { Chart } from 'chart.js';

import { Json } from '../models/json';
import ClientConsumptionFooter from './modal/ClientConsumptionFooter';

const ClientConsumption = () => {
	const json = useRouteLoaderData('demoJsonLoader') as Json;
	const barChartRef = useRef<Chart | null>(null);
	const [barChartHeight, setBarChartHeight] = useState(700);
	const [fontSize, setFontSize] = useState(16);
	const [padding, setPadding] = useState(30);

	const {
		totalChartLabels,
		totalChartF0,
		totalChartF1,
		totalChartF2,
		totalChartF3,
		totalChartPotenza,
	} = useMemo(() => {
		const result = {
			totalChartLabels: [] as string[],
			totalChartF0: [] as number[],
			totalChartF1: [] as number[],
			totalChartF2: [] as number[],
			totalChartF3: [] as number[],
			totalChartPotenza: [] as number[],
		};

		if (json?.GraficoEle?.length) {
			json.GraficoEle.forEach((value) => {
				result.totalChartLabels.push(value.Mese);
				result.totalChartF0.push(+value.F0);
				result.totalChartF1.push(+value.F1);
				result.totalChartF2.push(+value.F2);
				result.totalChartF3.push(+value.F3);
				result.totalChartPotenza.push(+value.Potenza);
			});
		}

		return result;
	}, [json]);

	const paintBarChart = useCallback(() => {
		if (!totalChartLabels.length) {
			return;
		}

		if (barChartRef.current) {
			barChartRef.current.destroy();
		}

		const datasetF0 = {
			label: 'F0',
			data: totalChartF0,
			yAxisID: 'F',
			backgroundColor: 'rgba(153, 203, 255, 0.8)',
			borderColor: 'rgba(153, 203, 255, 1)',
			borderWidth: 2,
		};

		const datasetF1 = {
			label: 'F1',
			data: totalChartF1,
			yAxisID: 'F',
			backgroundColor: 'rgba(251, 99, 64, 0.8)',
			borderColor: 'rgba(251, 99, 64, 1)',
			borderWidth: 2,
		};

		const datasetF2 = {
			label: 'F2',
			data: totalChartF2,
			yAxisID: 'F',
			backgroundColor: 'rgba(254, 185, 105, 0.8)',
			borderColor: 'rgba(254, 185, 105, 1)',
			borderWidth: 2,
		};

		const datasetF3 = {
			label: 'F3',
			data: totalChartF3,
			yAxisID: 'F',
			backgroundColor: 'rgba(45, 206, 152, 0.8)',
			borderColor: 'rgba(45, 206, 152, 1)',
			borderWidth: 2,
		};

		barChartRef.current = new Chart('barChart', {
			type: 'bar',
			data: {
				labels: totalChartLabels,
				datasets: [
					datasetF0,
					datasetF1,
					datasetF2,
					datasetF3,
					{
						label: 'Potenza',
						data: totalChartPotenza,
						yAxisID: 'Potenza',
						borderColor: 'rgba(60, 60, 60, 1)',
						borderWidth: 2,
						type: 'line',
					},
				],
			},
			options: {
				scales: {
					x: {
						stacked: true,
						ticks: {
							font: {
								size: fontSize,
								family: 'Montserrat',
							},
						},
					},
					F: {
						position: 'left',
						stacked: true,
						title: {
							display: true,
							text: 'kWh',
							font: {
								size: fontSize,
								family: 'Montserrat',
								style: 'normal',
							},
							padding,
						},
						ticks: {
							font: {
								size: fontSize,
								family: 'Montserrat',
								style: 'normal',
							},
							padding,
						},
					},
					Potenza: {
						position: 'right',
						title: {
							display: true,
							text: 'kW',
							padding,
							font: {
								size: fontSize,
								family: 'Montserrat',
								style: 'normal',
							},
						},
						ticks: {
							minRotation: 0,
							font: {
								size: fontSize,
								family: 'Montserrat',
								style: 'normal',
							},
							padding,
						},
					},
				},
				maintainAspectRatio: false,
				plugins: {
					datalabels: {
						display: false,
					},
					legend: {
						display: true,
						position: 'bottom',
						labels: {
							usePointStyle: true,
							font: {
								family: 'Montserrat',
								size: fontSize,
							},
							padding,
						},
					},
					tooltip: {
						callbacks: {
							label: function (context: any) {
								const datasetLabel = context.dataset.label || '';

								if (datasetLabel === 'Potenza') {
									return (
										datasetLabel +
										': ' +
										(+context.formattedValue)
											.toFixed(2)
											.toString()
											.replace('.', ',') +
										' kW'
									);
								}

								return (
									datasetLabel +
									': ' +
									(+context.formattedValue)
										.toFixed(2)
										.toString()
										.replace('.', ',') +
									' kWh'
								);
							},
						},
						bodyFont: {
							family: 'Montserrat',
							size: fontSize,
						},
						titleFont: {
							family: 'Montserrat',
							size: fontSize,
						},
						footerFont: {
							family: 'Montserrat',
							size: fontSize,
						},
					},
				},
			},
		});
	}, [
		totalChartLabels,
		totalChartF0,
		totalChartF1,
		totalChartF2,
		totalChartF3,
		totalChartPotenza,
		fontSize,
		padding,
	]);

	const onResize = useCallback(() => {
		const screenWidth = window.innerWidth;

		if (screenWidth <= 816) {
			setFontSize(10);
			setPadding(15);
			setBarChartHeight(200);
		} else if (screenWidth <= 1100) {
			setFontSize(12);
			setPadding(15);
			setBarChartHeight(400);
		} else {
			setFontSize(16);
			setPadding(30);
			setBarChartHeight(500);
		}
	}, []);

	useEffect(() => {
		onResize();
		window.addEventListener('resize', onResize);

		return () => {
			window.removeEventListener('resize', onResize);
			if (barChartRef.current) {
				barChartRef.current.destroy();
				barChartRef.current = null;
			}
		};
	}, [onResize]);

	useEffect(() => {
		if (json?.GraficoEle?.length) {
			paintBarChart();
		}
	}, [json, paintBarChart]);

	return (
		<div className='mt-4'>
			<div className='m-3 pb-5'>
				<div
					className='card p-3 border border-3 border-success p-md-5'
					hidden={!json.GraficoEle}
				>
					<h3 className='green'>Consumi mensili</h3>
					<h5 className='black'>
						Il grafico riporta i consumi effettuati negli ultimi mesi suddivisi
						per fasce.
					</h5>
					<div className='mt-5 w-100' style={{ height: `${barChartHeight}px` }}>
						<canvas id='barChart'></canvas>
					</div>
				</div>
			</div>
			<ClientConsumptionFooter />
		</div>
	);
};

export default ClientConsumption;
